import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getMentionableUsers, notifyReview, batchAssignCandidates } from '../api';
import { User, Candidate } from '../types';

interface Props {
  candidates: Candidate[];
  onClose: () => void;
  onSent: () => void;
  autoAssignPositionId?: string;
  autoAssignPositionName?: string;
}

export default function FeishuNotifyModal({ candidates, onClose, onSent, autoAssignPositionId, autoAssignPositionName }: Props) {
  const [interviewers, setInterviewers] = useState<User[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getMentionableUsers().then(users => {
      const bound = users.filter((u: User) => u.feishu_open_id && u.status === 'active' && u.role !== 'referrer');
      setInterviewers(bound);
      setLoading(false);
    }).catch(() => {
      toast.error('获取面试官列表失败');
      setLoading(false);
    });
  }, []);

  // 未分配岗位、且当前处于某具体岗位视图时，发起评审会自动归入该岗位
  const unassignedIds = autoAssignPositionId ? candidates.filter(c => !c.position_id).map(c => c.id) : [];

  const toggleInterviewer = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      toast.error('请选择至少一位面试官');
      return;
    }
    setSending(true);
    try {
      // 先把未分配岗位的候选人归入当前选中的岗位
      if (autoAssignPositionId && unassignedIds.length > 0) {
        try {
          await batchAssignCandidates(unassignedIds, autoAssignPositionId);
          toast.success(`${unassignedIds.length} 位未分配岗位的候选人已归入【${autoAssignPositionName || '当前岗位'}】`);
        } catch {
          toast.error('部分候选人归岗失败，仍继续发送评审');
        }
      }
      const result = await notifyReview({
        candidate_ids: candidates.map(c => c.id),
        interviewer_ids: selectedIds,
      });
      toast.success(result.message);
      onSent();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '发送失败');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[520px] max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-modal-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">发送飞书评审通知</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Selected candidates summary */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              待评审候选人（{candidates.length}人）
            </label>
            <div className="flex flex-wrap gap-1.5">
              {candidates.map(c => (
                <span key={c.id} className="px-2.5 py-1 rounded-lg bg-primary-50 text-xs font-medium text-primary-700 border border-primary-100">
                  {c.name}
                  {c.position_name ? <span className="text-primary-400 ml-1">· {c.position_name}</span> : null}
                </span>
              ))}
            </div>
          </div>

          {/* Interviewer selection */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              选择面试官（已绑定飞书）
            </label>
            {loading ? (
              <div className="text-center py-4 text-gray-400 text-sm">加载中...</div>
            ) : interviewers.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-700 flex items-center gap-2">
                <AlertCircle size={16} />
                暂无已绑定飞书的面试官，请先在团队管理中绑定飞书账号
              </div>
            ) : (
              <div className="space-y-1.5">
                {interviewers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => toggleInterviewer(u.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left',
                      selectedIds.includes(u.id)
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    <div className={clsx(
                      'w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors',
                      selectedIds.includes(u.id)
                        ? 'border-primary-600 bg-primary-600'
                        : 'border-gray-300'
                    )}>
                      {selectedIds.includes(u.id) && <CheckCircle size={12} className="text-white" />}
                    </div>
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">
                      {u.display_name?.charAt(0) || u.username.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800">{u.display_name || u.username}</span>
                      <span className="ml-2 text-[10px] text-gray-400">
                        {u.role === 'admin' ? '管理员' : u.role === 'hr' ? 'HR' : '面试官'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auto-assign hint */}
          {autoAssignPositionId && unassignedIds.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 text-xs text-emerald-700">
              其中 <span className="font-semibold">{unassignedIds.length}</span> 位未分配岗位的候选人，发送后将自动归入【{autoAssignPositionName || '当前岗位'}】。
            </div>
          )}

          {/* Info */}
          <p className="text-[11px] text-gray-400">
            面试官将在飞书收到消息卡片，可以直接点击「通过」或「淘汰」按钮完成评审。
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            取消
          </button>
          <button
            onClick={handleSend}
            disabled={sending || selectedIds.length === 0}
            className="px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send size={14} />
            )}
            发送通知
          </button>
        </div>
      </div>
    </div>
  );
}
